import ApiClient from "src/utils/ApiClient";
import { handleValidationError } from "src/utils/ApiClient/handleValidationError";
import { HRToolsServices } from "src/utils/Api/services";
import { IFormValues } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsEdit";
import {
  ICalendar,
  INewCalendar,
} from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  IEmployeeAssignments,
  IExportDTO,
  ImportType,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  IAbsenceRequestDTO,
  IEmployeeDetails,
  IRequireCorrectionDTO,
  Status,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IParsedBalancesFormData } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsModifyBalance/AbsenceRecordsModifyBalanceForm";
import { IFeatureConfiguration } from "./api/useFeatureConfiguration";

const AbsenceApiClient = new ApiClient({
  baseURL: "/api/attendance-tracker/api/rest",
  headers: {},
}).client;

AbsenceApiClient.interceptors.response.use(
  (response) => response,
  (error) => handleValidationError(error)
);

export type ResponseTypeList<T> = {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
};

export const fetchAbsenceTypes = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
): Promise<IAbsenceType[]> => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.ABSENCE_TYPES}?ownerId=${companyId}`,
    {
      signal,
    }
  );
  return data;
};

export const changeStatusAbsenceType = async (
  absenceTypeId: string,
  active: boolean
) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/${HRToolsServices.ABSENCE_TYPES}/${absenceTypeId}`,
    { active }
  );
  return data;
};

export const editAbsenceType = async (
  absenceTypeId: string,
  formData: IAbsenceType,
  fullyEditable: boolean
) => {
  const { data } = await AbsenceApiClient.request({
    method: fullyEditable ? "put" : "patch",
    url: `/v1/${HRToolsServices.ABSENCE_TYPES}/${absenceTypeId}`,
    data: formData,
  });
  return data;
};

export const createAbsenceType = async (formData: IAbsenceType) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/${HRToolsServices.ABSENCE_TYPES}`,
    formData
  );
  return data;
};

export const fetchAbsenceType = async ({
  absenceTypeId,
  signal,
}: {
  absenceTypeId: string | undefined;
  signal: AbortSignal | undefined;
}): Promise<IAbsenceType> => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.ABSENCE_TYPES}/${absenceTypeId}`,
    {
      signal,
    }
  );
  return data;
};

export const fetchAbsenceGroups = async ({
  companyId,
  name,
  signal,
}: {
  companyId: string;
  name?: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.ABSENCE_GROUPS}`,
    {
      params: {
        ownerId: companyId,
        name,
      },
      signal,
    }
  );
  return data;
};

export const fetchAbsenceGroup = async ({
  groupId,
  signal,
}: {
  groupId: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.ABSENCE_GROUPS}/${groupId}`,
    {
      signal,
    }
  );
  return data;
};

export const createAbsenceGroup = async (formData: IFormValues) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/${HRToolsServices.ABSENCE_GROUPS}`,
    formData
  );

  return data;
};

export const editAbsenceGroup = async (
  groupId: string,
  formData: IFormValues
) => {
  const { data } = await AbsenceApiClient.put(
    `/v1/${HRToolsServices.ABSENCE_GROUPS}/${groupId}`,
    formData
  );
  return data;
};

export const changeStatusAbsenceGroup = async (
  groupId: string,
  active: boolean
) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/${HRToolsServices.ABSENCE_GROUPS}/${groupId}`,
    {
      active,
    }
  );
  return data;
};

export const fetchCalendars = async ({
  companyId,
  years = [],
  signal,
}: {
  companyId: string;
  years: string[];
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.CALENDARS}?ownerId=${companyId}&years=${years.join(
      ","
    )}`,
    {
      signal,
    }
  );
  return data;
};

export const fetchCalendar = async ({
  calendarId,
  signal,
}: {
  calendarId: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.CALENDARS}/${calendarId}`,
    {
      signal,
    }
  );
  return data;
};

export const createCalendar = async (formData: INewCalendar) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/${HRToolsServices.CALENDARS}`,
    formData
  );

  return data;
};

export const editCalendar = async (calendarId: string, formData: ICalendar) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/${HRToolsServices.CALENDARS}/${calendarId}`,
    formData
  );
  return data;
};

export const deleteCalendar = async (calendarId: string) => {
  const { data } = await AbsenceApiClient.delete(
    `/v1/${HRToolsServices.CALENDARS}/${calendarId}`
  );
  return data;
};

export const changeStatusCalendar = async (
  calendarId: string,
  active: boolean
) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/${HRToolsServices.CALENDARS}/${calendarId}`,
    {
      active,
    }
  );
  return data;
};

export const editEmployeeAssignments = async (
  employeeId: string,
  formData: IEmployeeAssignments
) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/${HRToolsServices.EMPLOYEES}/${employeeId}/assignments`,
    formData
  );
  return data;
};

export const fetchEmployeePatterns = async ({
  employeeId,
  signal,
}: {
  employeeId: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.EMPLOYEES}/${employeeId}/patterns`,
    {
      signal,
    }
  );
  return data;
};

export const fetchEmployeesRecords = async ({
  companyId,
  currentYear,
  page = 0,
  size = 10,
  sort = [],
  filters = [],
  signal,
}: {
  companyId: string;
  currentYear: string;
  page?: number;
  size?: number;
  sort?: string[];
  filters?: { field: string; value: string | string[] }[];
  signal: AbortSignal | undefined;
}) => {
  const params = [
    ["companyId", companyId],
    ["calendarYear", currentYear],
    ["page", String(page)],
    ["size", String(size)],
    ...sort.map((s) => ["sort", s]),
    ...filters.map(({ field, value }) => [field, value]),
  ];

  const query = new URLSearchParams(params as string[][]).toString();

  const { data } = await AbsenceApiClient.get(
    `/v1/admin/${HRToolsServices.EMPLOYEES}?${query}`,
    {
      signal,
    }
  );
  return data;
};

export const fetchEmployeeRecordsCalendars = async ({
  companyId,
  employeeId,
  years = [],
  signal,
}: {
  companyId: string;
  employeeId: string;
  years?: string[];
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${
      HRToolsServices.EMPLOYEES
    }/calendars?size=1&ownerId=${companyId}&employeeIds=${employeeId}&years=${years.join(
      ","
    )}`,
    {
      signal,
    }
  );
  return data;
};

export const fetchCompanyMainAbsenceType = async ({
  companyId,
  signal,
}: {
  companyId: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.COMPANIES}/${companyId}/main-absence-type`,
    {
      signal,
    }
  );
  return data;
};

export const saveCompanyMainAbsenceType = async (
  companyId: string,
  absenceTypeId: string | null
) => {
  const { data } = await AbsenceApiClient.put(
    `/v1/${HRToolsServices.COMPANIES}/${companyId}/main-absence-type`,
    { absenceTypeId }
  );
  return data;
};

export const fetchEmployeeBalances = async (
  employeeId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.EMPLOYEES}/${employeeId}/balances`,
    {
      signal,
    }
  );
  return data;
};

export const fetchAbsenceTypeBalances = async ({
  employeeId,
  absenceTypeId,
  billingCycles,
  signal,
}: {
  employeeId: string;
  absenceTypeId?: string;
  billingCycles: string[];
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/admin/employees/${employeeId}/balances/${absenceTypeId}`,
    {
      params: {
        billingCycles,
      },
      signal,
    }
  );
  return data;
};

export const modifyEmployeeBalances = async (
  employeeId: string,
  formData: IParsedBalancesFormData
) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/${HRToolsServices.EMPLOYEES}/${employeeId}/modify-balance`,
    formData
  );
  return data;
};

export const fetchEmployeeCalendars = async ({
  companyId,
  employeeId,
  years = [],
  signal,
}: {
  companyId: string;
  employeeId: string;
  years: string[];
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get("/v1/calendars", {
    params: {
      ownerId: companyId,
      employeeId,
      years: years.join(","),
    },
    signal,
  });
  return data;
};

export const fetchEmployee = async (employeeId: string) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/${HRToolsServices.EMPLOYEES}/${employeeId}`
  );

  return data;
};

export const fetchEmployeeAbsences = async ({
  employeeId,
  from,
  to,
  statuses = [],
}: {
  employeeId: string;
  from: string;
  to: string;
  statuses: Status[];
}) => {
  const { data } = await AbsenceApiClient.get("/v1/absences", {
    params: {
      employeeIds: employeeId,
      from,
      to,
      statuses: statuses.join(","),
    },
  });

  return data;
};

export const fetchAbsenceRequestApprovers = async ({
  pageParam,
  name,
  employeeId,
  absenceTypeId = "",
}: Record<string, unknown>): Promise<ResponseTypeList<IEmployeeDetails>> => {
  const { data } = await AbsenceApiClient.get(
    `/v1/absence-requests/approvers`,
    {
      params: {
        page: pageParam,
        name,
        employeeId,
        absenceTypeId,
      },
    }
  );

  return data;
};

export const fetchAbsenceRequestReplacers = async ({
  pageParam,
  name,
  employeeId,
  from,
  to,
  absenceTypeId = "",
}: Record<string, unknown>): Promise<ResponseTypeList<IEmployeeDetails>> => {
  const { data } = await AbsenceApiClient.get(
    `/v1/absence-requests/replacers`,
    {
      params: {
        page: pageParam,
        name,
        employeeId,
        absenceTypeId,
        from,
        to,
      },
    }
  );

  return data;
};

export const fetchManagedEmployees = async ({
  companyId,
  pageParam,
  name,
}: Record<string, unknown>) => {
  const { data } = await AbsenceApiClient.get(
    "/v1/admin/absence-requests/managed-employees",
    {
      params: {
        companyId,
        page: pageParam,
        name,
      },
    }
  );

  return data;
};

export const fetchEmployeeSearch = async ({
  companyId,
  pageParam,
  name,
}: Record<string, unknown>) => {
  const { data } = await AbsenceApiClient.get("/v1/admin/employees", {
    params: {
      companyId,
      page: pageParam,
      name,
      calendarYear: new Date().getFullYear().toString(),
    },
  });

  return data;
};

export const uploadMedia = async (file: File) => {
  const formData = new FormData();

  formData.append("file", file);

  const { data } = await AbsenceApiClient.post(
    "v1/absence-requests/attachments",
    formData
  );

  return data;
};

export const createAbsenceRequest = async (formData: IAbsenceRequestDTO) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/admin/absence-requests`,
    formData
  );

  return data;
};

export const editAbsenceRequest = async (
  absenceRequestId: string,
  formData: IAbsenceRequestDTO
) => {
  const { data } = await AbsenceApiClient.patch(
    `/v1/admin/absence-requests/${absenceRequestId}`,
    formData
  );

  return data;
};

export const fetchEmployeeAbsenceRequests = async ({
  employeeId,
  page = 0,
  size = 10,
  sort = [],
  filters = [],
  signal,
}: {
  employeeId: string;
  page: number;
  size: number;
  sort?: string[] | undefined;
  filters?: { field: string; value: string }[] | undefined;
  signal: AbortSignal | undefined;
}) => {
  const params = [
    ["employeeId", employeeId],
    ["page", String(page)],
    ["size", String(size)],
    ...sort.map((sort) => ["sort", sort]),
    ...filters.map(({ field, value }) => [field, value]),
  ];
  const query = new URLSearchParams(params).toString();

  const { data } = await AbsenceApiClient.get(
    `/v1/admin/absence-requests?${query}`,
    {
      signal,
    }
  );

  return data;
};

export const fetchEmployeeAbsenceRequest = async (
  absenceRequestId: string,
  signal?: AbortSignal
) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/admin/absence-requests/${absenceRequestId}`,
    { signal }
  );

  return data;
};

export const fetchAbsenceRequestHistory = async (
  absenceRequestId: string,
  signal?: AbortSignal
) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/absence-requests/${absenceRequestId}/history`,
    { signal }
  );

  return data;
};

export const fetchManagedAbsenceRequests = async ({
  companyId,
  page = 0,
  size = 10,
  sort = [],
  filters = [],
  signal,
}: {
  companyId: string;
  page: number;
  size: number;
  sort?: string[] | undefined;
  filters?: { field: string; value: string }[] | undefined;
  signal: AbortSignal | undefined;
}) => {
  const params = [
    ["companyId", companyId],
    ["page", String(page)],
    ["size", String(size)],
    ...sort.map((sort) => ["sort", sort]),
    ...filters.map(({ field, value }) => [field, value]),
  ];
  const query = new URLSearchParams(params).toString();

  const { data } = await AbsenceApiClient.get(
    `/v1/admin/absence-requests/managed?${query}`,
    {
      signal,
    }
  );

  return data;
};

export const downloadMedia = async ({
  scopeId,
  attachmentId,
}: Record<string, string>) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/absence-requests/${scopeId}/attachments/${attachmentId}`,
    { responseType: "blob" }
  );

  return data;
};

export const fetchExportFile = async (exportId: string) => {
  const { data, headers } = await AbsenceApiClient.get(
    `/v1/admin/exports/${exportId}/download`,
    { responseType: "blob" }
  );

  return { data, contentDisposition: headers["content-disposition"] };
};

export const acceptAbsenceRequests = async (absenceRequests: string[]) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/admin/absence-requests/accept`,
    {
      absenceRequests,
    }
  );

  return data;
};

export const fetchImportTemplate = async (importType: ImportType) => {
  const data = await AbsenceApiClient.get(`/v1/admin/import-templates`, {
    params: { importType },
    responseType: "blob",
  });

  return data;
};

export const rejectAbsenceRequests = async (absenceRequests: string[]) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/admin/absence-requests/reject`,
    {
      absenceRequests,
    }
  );

  return data;
};

export const requireCorrectionAbsenceRequest = async (
  absenceRequestId: string,
  formData: IRequireCorrectionDTO
) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/admin/absence-requests/${absenceRequestId}/require-correction`,
    formData
  );

  return data;
};

export const cancelAbsenceRequest = async (absenceRequestId: string) => {
  const { data } = await AbsenceApiClient.post(
    `/v1/admin/absence-requests/${absenceRequestId}/cancel`
  );

  return data;
};

export const fetchAbsenceTypeHistory = async (absenceTypeId: string) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/admin/absence-types/${absenceTypeId}/history`
  );

  return data;
};

export const fetchAbsenceTypeBalanceHistory = async ({
  absenceTypeId,
  employeeId,
  page = 0,
  size = 10,
  signal,
}: {
  absenceTypeId: string;
  employeeId: string;
  page: number;
  size: number;
  signal: AbortSignal | undefined;
}) => {
  const params = [
    ["absenceTypeId", absenceTypeId],
    ["page", String(page)],
    ["size", String(size)],
  ];
  const query = new URLSearchParams(params).toString();

  const { data } = await AbsenceApiClient.get(
    `/v1/employees/${employeeId}/absence-type-balance-history?${query}`,
    {
      signal,
    }
  );
  return data;
};

export const importAbsenceData = async (
  companyId: string,
  importType: ImportType,
  file: File
) => {
  const formData = new FormData();
  const requestData = {
    companyId,
    importType,
  };

  formData.append(
    "request",
    new Blob([JSON.stringify(requestData)], {
      type: "application/json",
    })
  );
  formData.append("file", file);

  const { data } = await AbsenceApiClient.post("/v1/imports", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return data;
};

export const fetchImportStatus = async (importId: string) => {
  const { data } = await AbsenceApiClient.get(`/v1/imports/${importId}`);

  return data;
};

export const exportAbsenceData = async (params: IExportDTO) => {
  const { data } = await AbsenceApiClient.post("/v1/admin/exports", params);

  return data;
};

export const fetchExportStatus = async (exportId: string) => {
  const { data } = await AbsenceApiClient.get(`/v1/admin/exports/${exportId}`);

  return data;
};

export const fetchEmployeeGroups = async (
  companyId: string,
  signal?: AbortSignal
) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/companies/${companyId}/employee-groups`,
    { signal }
  );

  return data;
};

export const fetchOrganizationUnits = async (
  companyId: string,
  signal?: AbortSignal
) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/companies/${companyId}/organization-units`,
    { signal }
  );

  return data;
};

export const fetchEmployeeAbsencePlan = async ({
  employeeId,
  year,
  signal,
}: {
  employeeId: string | undefined;
  year: string;
  signal: AbortSignal | undefined;
}) => {
  const { data } = await AbsenceApiClient.get("/v1/admin/absence-plans", {
    params: {
      employeeId,
      year,
    },
    signal,
  });
  return data;
};

export const fetchFeatureConfiguration = async (companyId: string) => {
  const { data } = await AbsenceApiClient.get(
    `/v1/admin/companies/${companyId}/features`,
    {}
  );

  return data;
};

export const saveFeatureConfiguration = async (
  companyId: string,
  formData: IFeatureConfiguration
) => {
  const { data } = await AbsenceApiClient.put(
    `/v1/admin/companies/${companyId}/features`,
    formData
  );

  return data;
};
